// src/features/system/devices/components/iot/IoTSharedLinksPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// IoT Shared Links — Admin panel for creating dynamic multi-device URLs.
//
// Flow:
//   1. Fetch registered devices (device.instance)
//   2. User selects devices via checkboxes
//   3. Optional label + TTL
//   4. Generate → POST /bff/shared-links with scope_type='iot-devices'
//   5. Show URL + copy button
//
// PAC Glass design system — dark glassmorphism, transparent blur surfaces.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import PresenceDot from '@/shared/components/ui/PresenceDot';
import {
  fetchPresenceApi,
  DEVICE_KEYS,
  type Device,
} from '../../api/iotDevicesApi';
import { useDevicesList } from '../../hooks/useDevicesList';
import {
  createSharedLink,
  listSharedLinks,
  revokeSharedLink,
  type SharedLinkRecord,
} from '@/features/shared-scope/api/sharedScopeApi';

// ── Helpers ──────────────────────────────────────────────────────────────

function timeAgo(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const ms = Date.now() - new Date(value).getTime();
    if (ms < 0) return 'just now';
    const sec = Math.floor(ms / 1000);
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    return `${Math.floor(hr / 24)}d ago`;
  } catch {
    return value ?? '—';
  }
}

// ── TTL Options ──────────────────────────────────────────────────────────

const TTL_OPTIONS = [
  { label: 'No expiry',  value: 0 },
  { label: '1 hour',     value: 1 },
  { label: '6 hours',    value: 6 },
  { label: '24 hours',   value: 24 },
  { label: '7 days',     value: 168 },
  { label: '30 days',    value: 720 },
];

// ── Component ────────────────────────────────────────────────────────────

export default function IoTSharedLinksPanel() {
  // ── State ────────────────────────────────────────────────────────────
  const [selectedDevices, setSelectedDevices] = useState<Set<string>>(new Set());
  const [label, setLabel]     = useState('');
  const [ttlHours, setTtlHours] = useState(0);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const [copied, setCopied]   = useState(false);
  const [filter, setFilter]   = useState('');

  // ── Queries ──────────────────────────────────────────────────────────
  const { data: devices = [], isLoading: loadingDevices } = useDevicesList({ refetchInterval: 30_000 });

  const { data: presence = [] } = useQuery({
    queryKey: DEVICE_KEYS.presence,
    queryFn: fetchPresenceApi,
    refetchInterval: 10_000,
  });

  const { data: existingLinks, refetch: refetchLinks } = useQuery({
    queryKey: ['shared-links', 'iot'],
    queryFn:  listSharedLinks,
    refetchInterval: 30_000,
  });

  const presenceMap = useMemo(
    () => new Map(presence.map(p => [p.device_id, p])),
    [presence],
  );

  // ── Filtered IoT links ──────────────────────────────────────────────
  const iotLinks = useMemo(
    () => (existingLinks?.items || []).filter(l => l.scope_type === 'iot-devices'),
    [existingLinks],
  );

  // ── Filtered device list ────────────────────────────────────────────
  const filteredDevices = useMemo(() => {
    if (!filter) return devices;
    const lc = filter.toLowerCase();
    return devices.filter(d =>
      d.device_id.toLowerCase().includes(lc) ||
      (d.label ?? '').toLowerCase().includes(lc) ||
      (d.device_type ?? '').toLowerCase().includes(lc),
    );
  }, [devices, filter]);

  // ── Create mutation ─────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: () =>
      createSharedLink({
        scope_type:  'iot-devices',
        scope_ns:    'iot',
        scope_keys:  Array.from(selectedDevices),
        label:       label || undefined,
        ttl_hours:   ttlHours || undefined,
      }),
    onSuccess: (record) => {
      setGeneratedUrl(`${window.location.origin}/s/${record.token}`);
      setCopied(false);
      refetchLinks();
    },
  });

  // ── Revoke mutation ─────────────────────────────────────────────────
  const revokeMutation = useMutation({
    mutationFn: (token: string) => revokeSharedLink(token),
    onSuccess: () => refetchLinks(),
  });

  // ── Handlers ────────────────────────────────────────────────────────
  const toggleDevice = (deviceId: string) => {
    setSelectedDevices(prev => {
      const next = new Set(prev);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
    setGeneratedUrl(null);
  };

  const toggleAll = () => {
    if (selectedDevices.size === filteredDevices.length) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(filteredDevices.map(d => d.device_id)));
    }
    setGeneratedUrl(null);
  };

  const handleCopy = async () => {
    if (!generatedUrl) return;
    await navigator.clipboard.writeText(generatedUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerate = () => {
    if (selectedDevices.size === 0) return;
    createMutation.mutate();
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden">
      <div className="flex gap-4 flex-1 min-h-0 overflow-hidden">
        {/* ── Left: Device Selector ──────────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col gap-3 overflow-hidden">
          {/* Filter + select all */}
          <div className="flex items-center gap-2 shrink-0">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter devices…"
              className="flex-1 px-3 py-1.5 rounded pac-glass-input text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:border-neutral-500"
            />
            <button
              onClick={toggleAll}
              className="px-3 py-1.5 text-xs rounded border border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-neutral-200 transition-colors shrink-0"
            >
              {selectedDevices.size === filteredDevices.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Device list */}
          <div className="flex-1 min-h-0 overflow-y-auto space-y-1 pr-1">
            {loadingDevices ? (
              <div className="text-neutral-500 text-sm py-4 text-center">Loading devices…</div>
            ) : filteredDevices.length === 0 ? (
              <div className="text-neutral-500 text-sm py-4 text-center">
                {filter ? 'No devices match filter.' : 'No devices registered.'}
              </div>
            ) : (
              filteredDevices.map(d => (
                <DeviceRow
                  key={d.device_id}
                  device={d}
                  online={presenceMap.get(d.device_id)?.online ?? false}
                  selected={selectedDevices.has(d.device_id)}
                  onToggle={() => toggleDevice(d.device_id)}
                />
              ))
            )}
          </div>

          {/* Selection count */}
          <div className="text-xs text-neutral-500 shrink-0">
            {selectedDevices.size} of {devices.length} device{devices.length !== 1 ? 's' : ''} selected
          </div>
        </div>

        {/* ── Right: Config + Generate ───────────────────────────────── */}
        <div className="w-80 shrink-0 flex flex-col gap-3">
          {/* Config panel */}
          <div className="pac-glass-raised rounded-lg border border-neutral-700/50 p-4 space-y-3">
            <h3 className="text-sm font-medium text-neutral-200">Generate Shared Link</h3>

            {/* Label */}
            <div className="space-y-1">
              <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Label (optional)</label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="IoT Dashboard — Production Floor"
                className="w-full px-3 py-1.5 rounded pac-glass-input text-sm text-neutral-200 placeholder-neutral-600 outline-none focus:border-neutral-500"
              />
            </div>

            {/* TTL */}
            <div className="space-y-1">
              <label className="text-[10px] text-neutral-500 uppercase tracking-wider">Link Expiry</label>
              <select
                value={ttlHours}
                onChange={(e) => setTtlHours(Number(e.target.value))}
                className="w-full px-3 py-1.5 rounded pac-glass-input text-sm text-neutral-200 outline-none focus:border-neutral-500 bg-transparent"
              >
                {TTL_OPTIONS.map(o => (
                  <option key={o.value} value={o.value} className="bg-neutral-900">{o.label}</option>
                ))}
              </select>
            </div>

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={selectedDevices.size === 0 || createMutation.isPending}
              className="w-full px-4 py-2 rounded pac-glass-btn text-sm font-medium text-white
                         bg-emerald-600/80 hover:bg-emerald-500/80 disabled:opacity-40 disabled:cursor-not-allowed
                         transition-colors"
            >
              {createMutation.isPending
                ? 'Generating…'
                : `Generate URL (${selectedDevices.size} device${selectedDevices.size !== 1 ? 's' : ''})`}
            </button>

            {createMutation.isError && (
              <div className="text-red-400 text-xs">
                Failed: {(createMutation.error as Error).message}
              </div>
            )}
          </div>

          {/* Generated URL */}
          {generatedUrl && (
            <div className="pac-glass-raised rounded-lg border border-emerald-500/30 p-4 space-y-2">
              <div className="text-[10px] text-emerald-400 uppercase tracking-wider">Share URL</div>
              <div className="text-xs text-neutral-300 font-mono break-all select-all leading-relaxed">
                {generatedUrl}
              </div>
              <button
                onClick={handleCopy}
                className="w-full px-3 py-1.5 rounded border border-emerald-500/30 text-xs text-emerald-400
                           hover:border-emerald-400/50 transition-colors"
              >
                {copied ? '✓ Copied!' : 'Copy to Clipboard'}
              </button>
            </div>
          )}

          {/* Existing IoT links */}
          {iotLinks.length > 0 && (
            <div className="pac-glass rounded-lg border border-neutral-700/50 p-3 space-y-2 overflow-y-auto max-h-48">
              <div className="text-[10px] text-neutral-500 uppercase tracking-wider">Active IoT Links</div>
              {iotLinks.map(link => (
                <LinkRow key={link.token} link={link} onRevoke={() => revokeMutation.mutate(link.token)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Device Row ───────────────────────────────────────────────────────────

function DeviceRow({ device, online, selected, onToggle }: {
  device:   Device;
  online:   boolean;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer group transition-colors
        ${selected ? 'pac-glass-raised border border-emerald-500/30' : 'pac-glass border border-transparent hover:border-neutral-700'}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="accent-emerald-500 w-3 h-3"
      />
      <PresenceDot online={online} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-neutral-200 truncate group-hover:text-neutral-100 transition-colors">
          {device.label || device.device_id}
        </div>
        {device.label && (
          <div className="text-[10px] text-neutral-600 font-mono truncate">{device.device_id}</div>
        )}
      </div>
      <span className="text-[10px] text-neutral-600 shrink-0">
        {device.device_type ?? '—'}
      </span>
    </label>
  );
}

// ── Link Row ─────────────────────────────────────────────────────────────

function LinkRow({ link, onRevoke }: { link: SharedLinkRecord; onRevoke: () => void }) {
  const deviceCount = link.scope_keys?.length ?? 1;
  return (
    <div className="flex items-center justify-between gap-2 py-1">
      <div className="min-w-0 flex-1">
        <div className="text-xs text-neutral-300 truncate">
          {link.label || `${deviceCount} device${deviceCount !== 1 ? 's' : ''}`}
        </div>
        <div className="text-[10px] text-neutral-600">
          {link.access_count} hits · {link.unique_visitors} visitors · {timeAgo(link.created_at)}
        </div>
      </div>
      <button
        onClick={onRevoke}
        className="text-[10px] text-red-400/60 hover:text-red-400 transition-colors shrink-0"
      >
        Revoke
      </button>
    </div>
  );
}
